import React, { useState, useEffect } from "react";
import SchoolDetail from "./SchoolDetail";
import ActivityDetail from "./ActivityDetail";
import AddActivitySheet from "./AddActivitySheet";

const PERSON_OPTIONS = ["Everyone", "Kimberly", "Adam", "Carissa", "Robert", "Family"];
const PERSON_COLORS = { Kimberly: "var(--rose)", Adam: "var(--sage)", Carissa: "var(--amber)", Robert: "#6B8EAD", Family: "var(--mid)" };

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

function SkeletonRow() {
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", alignItems: "flex-start" }}>
      <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="skeleton skeleton-line-long" style={{ height: 15 }} />
        <div className="skeleton skeleton-line-short" style={{ height: 12 }} />
      </div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function SchoolActivitiesPage({ onBack, onTabTap }) {
  const [tab, setTab] = useState("Schools");
  const [person, setPerson] = useState("Everyone");
  const [showPast, setShowPast] = useState(false);
  const [schools, setSchools] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showAddActivity, setShowAddActivity] = useState(false);

  useEffect(() => {
    fetch(`/.netlify/functions/notion?type=schools`)
      .then(r => r.json()).then(d => setSchools(d.items || []))
      .finally(() => setLoadingSchools(false));
  }, []);

  useEffect(() => {
    setLoadingActivities(true);
    // Server-side now handles merging Family activities when person is selected
    const p = person !== "Everyone" ? `&person=${person}` : "";
    fetch(`/.netlify/functions/notion?type=activities&past=${showPast}${p}`)
      .then(r => r.json()).then(d => setActivities(d.items || []))
      .finally(() => setLoadingActivities(false));
  }, [person, showPast]);

  if (selectedSchool) return <SchoolDetail school={selectedSchool} onBack={() => setSelectedSchool(null)} onTabTap={onTabTap} />;
  if (selectedActivity) return <ActivityDetail activity={selectedActivity} onBack={() => setSelectedActivity(null)} onTabTap={onTabTap} />;

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 0", borderBottom: "2px solid var(--border)" }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)", marginBottom: 14 }}>
            School & <span style={{ position: "relative" }}>Activities<span style={{ position: "absolute", bottom: -2, left: 0, width: "100%", height: 2, background: "var(--sage)", borderRadius: 2 }} /></span>
          </div>
          <div style={{ display: "flex" }}>
            {["Schools", "Activities"].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ flex: 1, textAlign: "center", padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit", borderBottom: `2.5px solid ${tab === t ? "var(--sage)" : "transparent"}`, color: tab === t ? "var(--sage)" : "var(--muted)" }}>{t}</button>
            ))}
          </div>
        </header>

        <div className="scroll-body">
          {tab === "Schools" && (
            <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
              {loadingSchools ? <><SkeletonRow /><SkeletonRow /></> : schools.length === 0 ? (
                <div className="empty-state"><div className="empty-text">No active schools</div></div>
              ) : schools.map(s => (
                <div key={s.id} onClick={() => setSelectedSchool(s)} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--amber-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0 }}>🏫</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{s.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: PERSON_COLORS[s.person] || "var(--muted)", marginTop: 2 }}>{s.person}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                      {s.startTime && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "var(--amber-muted)", color: "var(--amber)" }}>{s.startTime}</span>}
                      {s.dismissalTime && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "var(--amber-muted)", color: "var(--amber)" }}>{s.dismissalTime}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0, paddingTop: 2 }}>
                    {s.officeEmail && <a href={`mailto:${s.officeEmail}`} onClick={e => e.stopPropagation()} style={{ fontSize: 20, textDecoration: "none" }}>✉️</a>}
                    <span style={{ color: "var(--border)", fontSize: 20 }}>›</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "Activities" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px" }}>
                <select value={person} onChange={e => setPerson(e.target.value)} style={{ flex: 1, appearance: "none", WebkitAppearance: "none", background: "var(--cream-hover)", border: "1.5px solid var(--border)", borderRadius: 20, padding: "6px 14px", fontSize: 13, fontWeight: 700, color: "var(--charcoal)", fontFamily: "inherit", cursor: "pointer" }}>
                  {PERSON_OPTIONS.map(p => <option key={p}>{p}</option>)}
                </select>
                <div style={{ display: "flex", background: "var(--cream-hover)", borderRadius: 20, padding: 2, flexShrink: 0 }}>
                  {["Active", "Past"].map(s => (
                    <button key={s} onClick={() => setShowPast(s === "Past")} style={{ padding: "4px 12px", borderRadius: 18, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "none", fontFamily: "inherit", background: (s === "Past") === showPast ? "var(--white)" : "none", color: (s === "Past") === showPast ? "var(--charcoal)" : "var(--muted)", boxShadow: (s === "Past") === showPast ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>{s}</button>
                  ))}
                </div>
                <button onClick={() => setShowAddActivity(true)} style={{ background: "var(--sage)", border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>+ Add</button>
              </div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
                {loadingActivities ? (
                  <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
                ) : activities.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">{showPast ? "📦" : "🏃"}</div>
                    <div className="empty-text">{showPast ? "No past activities" : "No active activities"}</div>
                  </div>
                ) : activities.map(act => {
                  const isEmoji = act.icon && !act.icon.startsWith("http");
                  const isImgUrl = act.icon && act.icon.startsWith("http");
                  return (
                    <div key={act.id} onClick={() => setSelectedActivity(act)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer", opacity: showPast ? 0.6 : 1 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--rose-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0, overflow: "hidden" }}>
                        {isImgUrl ? <img src={act.icon} alt="" style={{ width: 40, height: 40, objectFit: "cover" }} /> : isEmoji ? act.icon : "🏃"}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{act.name}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--muted)", marginTop: 2 }}>{act.person}</div>
                        {(act.seasonStart || act.seasonEnd) && (
                          <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500, marginTop: 2 }}>
                            {act.seasonEnd ? `Ends ${formatDate(act.seasonEnd)}` : act.seasonStart ? `Started ${formatDate(act.seasonStart)}` : ""}
                          </div>
                        )}
                        {act.location && <div style={{ fontSize: 13, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>📍 {act.location}</div>}
                        {act.schedule && <div style={{ fontSize: 13, color: "var(--mid)", fontWeight: 500, marginTop: 4, lineHeight: 1.4 }}>{act.schedule}</div>}
                      </div>
                      <span style={{ color: "var(--border)", fontSize: 18, flexShrink: 0, marginTop: 2 }}>›</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: t.id === "school" ? 800 : 700, color: t.id === "school" ? "var(--sage)" : "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
              {t.id === "school" && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--sage)", marginTop: 1 }} />}
            </div>
          ))}
        </nav>
      </div>

      {showAddActivity && (
        <AddActivitySheet
          onClose={() => setShowAddActivity(false)}
          onAdd={(item) => {
            setActivities(prev => [item, ...prev]);
            setShowPast(false);
            setShowAddActivity(false);
          }}
          defaultPerson={person !== "Everyone" ? person : ""}
          lockPerson={false}
        />
      )}
    </>
  );
}
